import { useCallback, useEffect, useState } from "react";
import type { Activity } from "api";
import { activityApi } from "api";
import { isForbidden, isUnauthenticated } from "utils/error.utils";
import type { LoadStatus } from "./useProfile";

type UseActivity = {
  activity: Activity[];
  status: LoadStatus;
  /** Fetch again in the background, keeping what is on screen until it answers. */
  reload: () => void;
};

/**
 * The last things that happened in the account. Company-scoped, so a
 * colleague's action appears here too — `actor` says whose it was.
 *
 * An empty feed is a legitimate answer: it drives the "nothing yet" state
 * rather than an error.
 */
export const useActivity = (limit: number, onSessionLost: () => void): UseActivity => {
  const [activity, setActivity] = useState<Activity[]>([]);
  const [status, setStatus] = useState<LoadStatus>("loading");

  const load = useCallback(
    (background: boolean) => {
      let cancelled = false;

      activityApi
        .list(limit)
        .then(({ data }) => {
          if (cancelled) return;
          setActivity(data.data);
          setStatus("ready");
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          if (isUnauthenticated(error)) {
            onSessionLost();
            setStatus("unauthorized");
            return;
          }
          // A refresh that fails leaves the last good feed alone: a blip is not
          // worth replacing a readable card with an error.
          if (background) return;

          setStatus(isForbidden(error) ? "unverified" : "failed");
        });

      return () => {
        cancelled = true;
      };
    },
    [limit, onSessionLost]
  );

  useEffect(() => load(false), [load]);

  const reload = useCallback(() => {
    load(true);
  }, [load]);

  return { activity, status, reload };
};
